// @ts-nocheck
import { Injectable } from '@angular/core';
import { OLMapService } from './ol-map.service';
import { Feature, MapBrowserEvent } from 'ol';
import { Geometry } from 'ol/geom.js';
import { HttpClient } from '@angular/common/http';
import { OlAtlasBaseService } from './ol-atlas-base.service';
import * as TileLayerModule from 'ol/layer/Tile.js';
const TileLayer = TileLayerModule.default as typeof TileLayerModule.default;
import * as TileWMSModule from 'ol/source/TileWMS.js';
const TileWMS = TileWMSModule.default as typeof TileWMSModule.default;
import { BehaviorSubject, Observable } from 'rxjs';
import { Router, ActivatedRoute } from '@angular/router';
import { DatasetEntry } from 'src/app/models/dataset-entry';
import { transformArrayToLabel } from 'src/app/utils/transformArrayToLabel';
import { Filter } from 'src/app/datasets/filter';
import { DOMAIN } from 'src/app/models/domain/domain';
import { AtlasMobileAreaDataset } from 'src/app/datasets/atlas-mobile-area.dataset';
import { AtlasMobileGridDataset } from 'src/app/datasets/atlas-mobile-grid.dataset';
import { checkCorrectParams, checkQueryParameters } from 'src/app/utils/helpers';
import {
    BBOX,
    DEFAULT_EXTENT,
    MOBILE_DATASETS,
    MOBILE_DATASET_LABELS,
    MOBILE_FILTERS,
    MOBILE_FILTER_LABELS,
    SELECTEDTAB,
} from 'src/assets/constants/constants';
import { environment } from 'src/environments/environment';
import { Coordinate } from 'ol/coordinate.js';
import { FeatureCollection } from 'src/app/utils/geojson';
import { transform } from 'ol/proj.js';

@Injectable({
    providedIn: 'root',
})
export class AtlasMobileService extends OlAtlasBaseService {
    isByzoneLoaded: boolean = false;
    isDetailedLoaded: boolean = false;
    layerNameMap = new Map<string, number | string>([
        ['Satisfactory', 1],
        ['Good', 2],
        ['VeryGood', 3],
        ['territory', 't'],
        ['households', 'h'],
        ['population', 'p'],
        ['DISTRICT', 'arrondissement'],
    ]);
    constructor(
        private http: HttpClient,
        public olMapService: OLMapService,
        private router: Router,
        private route: ActivatedRoute
    ) {
        super(olMapService);
    }

    mapOperations(event: MapBrowserEvent<MouseEvent>) {
        return this.createJoinEndpoints(event);
    }
    createJoinEndpoints(event: MapBrowserEvent<MouseEvent>): { layer: any; featureCollection: Observable<Object> } {
        const tileLayer = event.map
            .getLayers()
            .getArray()
            .find((l) => l instanceof TileLayer && l.getSource() instanceof TileWMS && l.getVisible()) as TileLayer<TileWMS>;
        if (!tileLayer) {
            return;
        }

        const selectedLayerName = tileLayer.getSource().getParams().LAYERS as string;
        if (!selectedLayerName) {
            return;
        }
        const view = this.olMapService.map.getView();
        const projection = view.getProjection();
        const coordinate = event.coordinate;
        const targetProj = 'EPSG:31370';
        const transformedCoord = transform(coordinate, projection, targetProj);
        const style = this.getStyleName();
        if (style.includes(MOBILE_DATASETS[0])) {
            const wfsUrl = this.buildWFSUrl(transformedCoord, targetProj, selectedLayerName);
            return {
                layer: selectedLayerName,
                featureCollection: this.http.get<FeatureCollection>(wfsUrl),
            };
        }

        // The CQL geometry literal is evaluated in the native CRS of the merged
        // by-zone layer (EPSG:31370). Keep the response in the map projection so
        // returned features can be rendered directly by OpenLayers.
        const mapProjection = projection.getCode() || 'EPSG:3857';
        const wfsUrl = this.buildWFSUrlStatSec(transformedCoord, mapProjection);
        return {
            layer: `statsec_${environment.geoServer.dp_mobile_byzone_overview_merged}:${this.getLayerName()}`,
            featureCollection: this.http.get<FeatureCollection>(wfsUrl),
        };
    }

    buildWFSUrl(coord: Coordinate, projection: string, layerName: string): string {
        const [x, y] = coord;
        const srs = projection || 'EPSG:3857';
        const cqlFilter = encodeURIComponent(`INTERSECTS(geom, POINT(${x} ${y}))`);

        return (
            `${environment.geoServer.baseUrl}/wfs?` +
            `service=WFS&version=1.1.0&request=GetFeature&typeName=${layerName}` +
            `&outputFormat=application/json&srsName=${srs}&CQL_FILTER=${cqlFilter}`
        );
    }
    buildWFSUrlStatSec(coord: Coordinate, projection: string): string {
        const [x, y] = coord;
        const layer = this.getLayerName();
        const layerName = `${environment.geoServer.dp_mobile_byzone_overview_merged}:${layer}`;
        const srs = projection || 'EPSG:3857';
        const technology = this.olMapService.filter.get(MOBILE_FILTERS[1]);
        const operator = this.olMapService.filter.get(MOBILE_FILTERS[0]) as string;
        const level = this.getSelectedAreaLevelForSelection();
        const levelFilter = this.getLevelFilterForSelection(level);
        const cqlFilter = encodeURIComponent(`${levelFilter} AND technology ='${technology}' AND operator ='${operator.toUpperCase()}' AND
                INTERSECTS(geom, POINT(${x} ${y}))
            `);

        return (
            `${environment.geoServer.baseUrl}/wfs?` +
            `service=WFS&version=1.1.0&request=GetFeature&typeName=${layerName}` +
            `&outputFormat=application/json&srsName=${srs}&CQL_FILTER=${cqlFilter}`
        );
    }

    getSelectedAreaLevelForSelection(): string | null {
        let level = this.olMapService.filter.get(MOBILE_FILTERS[3]);
        if (level && this.layerNameMap.has(level)) {
            level = this.layerNameMap.get(level) as string | number;
        }

        if (level === undefined || level === null) {
            return null;
        }

        return level.toString().toLowerCase();
    }

    private getLevelFilterForSelection(level: string | null): string {
        const selectionLevels: Record<string, string[]> = {
            statistical_sector: ['statistical_sector', 'municipality', 'province'],
            municipality: ['municipality', 'province'],
            arrondissement: ['arrondissement', 'province'],
            province: ['province', 'region'],
            region: ['region', 'country'],
            country: ['country'],
        };
        const levels = selectionLevels[level] ?? (level ? [level] : []);

        if (!levels.length) {
            return `level IN ('statistical_sector','municipality','province')`;
        }

        return `level IN (${levels.map((entry) => `'${entry}'`).join(',')})`;
    }

    getLayerDetails(feature: Feature<Geometry>): void {
        this.olMapService.detailsLoading = true;
        this.olMapService.details = [];
        const style = this.getStyleName();
        feature
            .getKeys()
            .sort()
            .forEach((key) => {
                const column = style.toLowerCase().replace('satisfactory', '1').replace('verygood', '3').replace('good', '2');
                const name = 'name';

                if (!name.localeCompare(key) && feature.get(key) !== '' && feature.get(key) !== null) {
                    this.olMapService.details.push({
                        label: name,
                        value: feature.get(key),
                    });
                }
                if (!column.localeCompare(key) && feature.get(key) !== '' && feature.get(key) !== null) {
                    this.olMapService.details.push({
                        label: 'cover',
                        value: feature.get(key),
                    });
                }
            });

        this.olMapService.detailsLoading = false;
    }
    getStatSecDetails(feature: Feature<Geometry>): void {
        this.olMapService.detailsLoading = true;
        this.olMapService.details = [];
        const style = this.getStyleName();
        feature
            .getKeys()
            .sort()
            .forEach((key) => {
                const column = style.toLowerCase().replace('satisfactory', '1').replace('verygood', '3').replace('good', '2');
                const name = 'name';

                if (!name.localeCompare(key) && feature.get(key) !== '' && feature.get(key) !== null) {
                    this.olMapService.details.push({
                        label: '',
                        level: 'MUNICIPALITY',
                        value: feature.get('municipalityName'),
                    });
                    this.olMapService.details.push({
                        label: name,
                        value: feature.get(key),
                    });
                }
                if (!column.localeCompare(key) && feature.get(key) !== '' && feature.get(key) !== null) {
                    this.olMapService.details.push({
                        label: 'cover',
                        value: feature.get(key),
                    });
                }
            });

        this.olMapService.detailsLoading = false;
    }
    getLayerName(): string | null {
        const quality = this.olMapService.filter.get(MOBILE_FILTERS[2]) as string;
        const coverType = this.olMapService.filter.get(MOBILE_FILTERS[4]) as string;
        const qualityMap = this.layerNameMap.get(quality);
        const coverMap = this.layerNameMap.get(coverType);

        if (!quality || !coverType || qualityMap === undefined || coverMap === undefined) {
            console.error('[Atlas Mobile] Missing or unsupported by-zone filters:', { quality, coverType });
            return null;
        }

        return `byzone_${coverMap}${qualityMap}`;
    }
    getSelectedCoverageValue(feature: Feature<Geometry>): number | undefined {
        const coverType = this.olMapService.filter.get(MOBILE_FILTERS[4]) as string;
        const quality = this.olMapService.filter.get(MOBILE_FILTERS[2]) as string;
        const qualityValue = this.layerNameMap.get(quality);
        const value = Number(feature.get('filtervalue') ?? feature.get(`${coverType}_${qualityValue}`));
        return Number.isFinite(value) ? value : undefined;
    }

    private getByzoneLayerName(): string | null {
        const operator = this.olMapService.filter.get(MOBILE_FILTERS[0]) as string;
        const technology = this.olMapService.filter.get(MOBILE_FILTERS[1]) as string;
        const level = this.olMapService.filter.get(MOBILE_FILTERS[3]) as string;
        return operator && technology && level ? `${operator}_${level}_${technology}` : null;
    }
    getStyleName(): string {
        const quality = this.olMapService.filter?.get(MOBILE_FILTERS[2]) as string;
        const coverType = this.olMapService.filter?.get(MOBILE_FILTERS[4]) as string;

        if (coverType && quality) {
            const style = coverType.toLowerCase() + '_' + quality;
            return style;
        }
        return 'mobile_detailed';
    }
    loadLayer(): void {
        const operator = this.olMapService.filter.get(MOBILE_FILTERS[0]) as string;
        const technology = this.olMapService.filter.get(MOBILE_FILTERS[1]) as string;
        const styleName = this.getStyleName();

        if (styleName === 'mobile_detailed') {
            this.removeVectorLayers();
            this.olMapService.closeDetailPanel();
        }

        const workspace =
            styleName === 'mobile_detailed' ? environment.geoServer.dp_mobile_detailed_overview : environment.geoServer.dp_mobile_byzone_overview;
        const publishedLayerName = styleName === 'mobile_detailed' ? `${operator}_${technology}` : this.getByzoneLayerName();

        if (!publishedLayerName) {
            console.warn('[Atlas Mobile] Cannot load layer - missing required filters.', { operator, technology, styleName });
            return;
        }

        const selectedLayerName = `${workspace}:${publishedLayerName}`;
        const selectedStyleName = styleName === 'mobile_detailed' ? '' : styleName;
        const existingLayer = this.olMapService.layers.find((layer) => layer.name === selectedLayerName);

        this.removeVectorLayers();
        if (existingLayer) {
            const source = existingLayer.layer.getSource();
            if (source instanceof TileWMS) {
                source.updateParams({ STYLES: selectedStyleName });
            }
            existingLayer.layer.setVisible(true);
        } else {
            const tileLayer = new TileLayer({
                source: new TileWMS({
                    url: `${environment.geoServer.baseUrl}/${workspace}/wms`,
                    hidpi: false,
                    params: {
                        LAYERS: selectedLayerName,
                        STYLES: selectedStyleName,
                        FORMAT: 'image/png',
                        TILED: true,
                        VERSION: '1.1.1',
                    },
                    serverType: 'geoserver',
                }),
                opacity: 0.6,
                transition: 0,
            });
            tileLayer.set('layerdetails', { name: selectedLayerName, workspace });
            this.olMapService.map.addLayer(tileLayer);
            this.olMapService.layers.push({ name: selectedLayerName, layer: tileLayer, workspace: 'polygon' });
        }

        this.olMapService.layers.forEach((entry) => entry.layer.setVisible(entry.name === selectedLayerName));
        this.changeUrl();
    }
    setFilter(): void {
        this.loadLayer();
    }

    updateDetails(feature: Feature<Geometry>) {
        this.olMapService.detailsLoading = true;
        this.getLayerDetails(feature);
        this.olMapService.detailsLoading = false;
    }

    updateStatSecDetails(feature: Feature<Geometry>) {
        this.olMapService.detailsLoading = true;
        this.getStatSecDetails(feature);
        this.olMapService.detailsLoading = false;
    }

    changeDataset(entry: DatasetEntry): void {
        if (!entry) {
            return;
        }

        const isActiveDataset =
            this.olMapService.dataset === entry.dataset && this.olMapService.filter === entry.filter && this.olMapService.filters === entry.filters;
        const hasVisibleLayer = this.olMapService.layers.some((layer) => layer.layer.getVisible());
        if (isActiveDataset && hasVisibleLayer) {
            return;
        }

        this.olMapService.datasetEntry = entry;
        this.olMapService.dataset = entry.dataset;
        this.olMapService.filter = entry.filter;
        this.olMapService.filters = entry.filters;
        this.loadLayer();
    }

    setDefault(index: number) {
        const defaultDataset = this.olMapService.datasets[index];
        this.changeDataset(defaultDataset);
    }
    setDefaultFilter() {
        if (!Object.keys(this.olMapService.mapConfig)) {
            this.setDefault(0);
            return;
        }
        const selectedTab = this.olMapService.mapConfig[SELECTEDTAB];
        if (!selectedTab || selectedTab == '') {
            this.setDefault(0);
            return;
        }
        switch (selectedTab) {
            case MOBILE_DATASETS[0]:
                const isDetailedParamsCorrect = this.checkDetailedParamsCorrect([MOBILE_FILTERS[0], MOBILE_FILTERS[1]]);
                if (!isDetailedParamsCorrect) {
                    this.setDefault(0);
                    break;
                }
                this.setDetailedFilter(selectedTab);
                break;
            case MOBILE_DATASETS[1]:
                const isByzoneParamsCorrect = this.checkByzoneParamsCorrect(MOBILE_FILTERS);
                if (!isByzoneParamsCorrect) {
                    this.setDefault(0);
                    break;
                }
                this.setByzoneFilter(selectedTab);
                break;
            default:
                this.setDefault(0);
        }
    }
    checkByzoneParamsCorrect(filters: string[]): boolean {
        const haveAllQueryParams = checkQueryParameters(this.olMapService.mapConfig, filters);
        if (!haveAllQueryParams) {
            return false;
        }

        const isCorrectParams = checkCorrectParams(this.olMapService.datasets[1].filters, this.olMapService.mapConfig, filters);
        if (isCorrectParams.some((cp) => cp == false)) {
            return false;
        }
        return true;
    }
    setByzoneFilter(selectedTab: string) {
        this.olMapService.filter.set(MOBILE_FILTERS[0], this.olMapService.mapConfig[MOBILE_FILTERS[0]]);
        this.olMapService.filter.set(MOBILE_FILTERS[1], this.olMapService.mapConfig[MOBILE_FILTERS[1]]);
        this.olMapService.filter.set(MOBILE_FILTERS[2], this.olMapService.mapConfig[MOBILE_FILTERS[2]]);
        this.olMapService.filter.set(MOBILE_FILTERS[3], this.olMapService.mapConfig[MOBILE_FILTERS[3]]);
        this.olMapService.filter.set(MOBILE_FILTERS[4], this.olMapService.mapConfig[MOBILE_FILTERS[4]]);

        const bBoxArr = this.olMapService.mapConfig[BBOX] ? this.olMapService.mapConfig[BBOX].split(',').map((bbox) => Number(bbox)) : '';
        if (bBoxArr != '') {
            this.olMapService.map.getView().fit(bBoxArr);
        }
        const byzoneDataset = this.olMapService.datasets.find((ds) => ds.label.includes(selectedTab));
        byzoneDataset.filter = this.olMapService.filter;
        this.changeDataset(byzoneDataset);
    }
    checkDetailedParamsCorrect(filters: string[]): boolean {
        const haveAllQueryParams = checkQueryParameters(this.olMapService.mapConfig, filters);
        if (!haveAllQueryParams) {
            return false;
        }
        const isCorrectParams = checkCorrectParams(this.olMapService.datasets[0].filters, this.olMapService.mapConfig, filters);
        if (isCorrectParams.some((cp) => cp == false)) {
            return false;
        }
        return true;
    }
    setDetailedFilter(selectedTab: string) {
        this.olMapService.filter.set(MOBILE_FILTERS[0], this.olMapService.mapConfig[MOBILE_FILTERS[0]]);
        this.olMapService.filter.set(MOBILE_FILTERS[1], this.olMapService.mapConfig[MOBILE_FILTERS[1]]);
        const bBoxArr = this.olMapService.mapConfig[BBOX] ? this.olMapService.mapConfig[BBOX].split(',').map((bbox) => Number(bbox)) : '';
        if (bBoxArr != '') {
            this.olMapService.map.getView().fit(bBoxArr);
        }
        const detailedDataset = this.olMapService.datasets.find((ds) => ds.label.includes(selectedTab));
        detailedDataset.filter = this.olMapService.filter;
        this.changeDataset(detailedDataset);
    }
    changeUrl() {
        // Calculate bbox safely
        const mapSize = this.olMapService.map.getSize();
        let bboxValue: string;

        if (mapSize && mapSize[0] > 0 && mapSize[1] > 0) {
            try {
                const extent = this.olMapService.map.getView().calculateExtent(mapSize);
                // Validate extent coordinates are reasonable (not MAX_INT overflow)
                if (extent.every((coord) => isFinite(coord) && Math.abs(coord) < 90000000)) {
                    bboxValue = extent.toString();
                } else {
                    console.warn('[Atlas Mobile] Invalid extent calculated, using default');
                    bboxValue = DEFAULT_EXTENT.toString();
                }
            } catch (e) {
                console.error('[Atlas Mobile] Error calculating extent:', e);
                bboxValue = DEFAULT_EXTENT.toString();
            }
        } else {
            console.warn('[Atlas Mobile] Invalid map size, using default extent');
            bboxValue = DEFAULT_EXTENT.toString();
        }

        this.router.navigate([], {
            relativeTo: this.route,
            queryParams: {
                [SELECTEDTAB]: this.olMapService.datasetEntry?.label.split('.')[1],
                [MOBILE_FILTERS[0]]: this.olMapService.filter.get(MOBILE_FILTERS[0]),
                [MOBILE_FILTERS[1]]: this.olMapService.filter.get(MOBILE_FILTERS[1]),
                [MOBILE_FILTERS[2]]: this.olMapService.filter.get(MOBILE_FILTERS[2]),
                [MOBILE_FILTERS[3]]: this.olMapService.filter.get(MOBILE_FILTERS[3]),
                [MOBILE_FILTERS[4]]: this.olMapService.filter.get(MOBILE_FILTERS[4]),
                [BBOX]: bboxValue,
            },
            replaceUrl: true,
        });
    }
    detailedSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    byzoneSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    setDetailed() {
        // Filter values are static; a capabilities request here delayed first paint and was discarded.
        {
            // const featureDescriptor = this.olMapService.readWfsCapabilities(val);

            // TODO: use featureDescriptor to get unique values
            const uniqueProviders = ['Telenet', 'Proximus', 'Orange'];
            const uniqueTechnologies = ['4G', '5G'];

            const providerArr = transformArrayToLabel(uniqueProviders, true, 'labels.');
            const technologyArr = transformArrayToLabel(uniqueTechnologies, true);

            const detailed = this.olMapService.datasets.find((ds) => ds.label == MOBILE_DATASET_LABELS[0]);
            const detailedProviders = detailed.filters.find((fl) => fl.key == MOBILE_FILTERS[0]);
            const detailedTechnologies = detailed.filters.find((fl) => fl.key == MOBILE_FILTERS[1]);

            detailedProviders.choices = providerArr;
            detailedTechnologies.choices = technologyArr;
            const technologyIndex = technologyArr.length - 1;

            detailed.filter = new Filter({
                provider: providerArr[0].value,
                technology: technologyArr[technologyIndex].value,
            });
            this.olMapService.filters = detailed.filters;
        }
        this.isDetailedLoaded = true;
        this.detailedSubject.next(true);
    }

    setByzone() {
        // The configured filters below are the source of truth, so avoid an unused capabilities call.
        {
            // const featureDescriptor = this.olMapService.readWfsCapabilities(val);
            const uniqueProviders = ['Telenet', 'Orange', 'Proximus'];
            const uniqueLevels = ['STATISTICAL_SECTOR', 'MUNICIPALITY', 'DISTRICT', 'PROVINCE', 'REGION', 'Country'];
            const uniqueTechnologies = ['4G', '5G'];
            const uniqueCovers = ['households', 'population', 'territory'];
            const uniqueQualities = ['Satisfactory', 'Good', 'VeryGood'];

            const providerArr = transformArrayToLabel(uniqueProviders);
            const levelArr = transformArrayToLabel(uniqueLevels, true, 'area.level.');
            const technologyArr = transformArrayToLabel(uniqueTechnologies, true);
            const coverArr = transformArrayToLabel(uniqueCovers, true, 'labels.');
            const qualityArr = transformArrayToLabel(uniqueQualities, false, 'labels.');

            const byzone = this.olMapService.datasets.find((ds) => ds.label == MOBILE_DATASET_LABELS[1]);
            const byzoneProviders = byzone.filters.find((fl) => fl.key == MOBILE_FILTERS[0]);
            const byzoneTechnologies = byzone.filters.find((fl) => fl.key == MOBILE_FILTERS[1]);
            const byzoneQualities = byzone.filters.find((fl) => fl.key == MOBILE_FILTERS[2]);
            const byzoneLevels = byzone.filters.find((fl) => fl.key == MOBILE_FILTERS[3]);
            const byzoneCovers = byzone.filters.find((fl) => fl.key == MOBILE_FILTERS[4]);

            byzoneProviders.choices = providerArr;
            byzoneLevels.choices = levelArr;
            byzoneTechnologies.choices = technologyArr;
            byzoneCovers.choices = coverArr;
            DOMAIN.mobileQualities.forEach((qa) => {
                qualityArr.map((te) => {
                    if (qa.value.toString() == te.value) {
                        te.label = qa.label;
                    }
                });
            });
            byzoneQualities.choices = qualityArr;
            const index = technologyArr.length - 1;

            byzone.filter = new Filter({
                provider: providerArr[0].value,
                technology: technologyArr[index].value,
                quality: qualityArr[1].value,
                level: levelArr[0].value,
                coverType: coverArr[1].value,
            });
        }
        this.isByzoneLoaded = true;
        this.byzoneSubject.next(true);
    }
    setDefaultParams() {
        this.isDetailedLoaded = false;
        this.isByzoneLoaded = false;
        this.detailedSubject.next(false);
        this.byzoneSubject.next(false);

        this.olMapService.datasets = [
            {
                label: MOBILE_DATASET_LABELS[0],
                dataset: new AtlasMobileGridDataset(),
                filter: new Filter(),
                filters: [
                    {
                        label: MOBILE_FILTER_LABELS[0],
                        key: MOBILE_FILTERS[0],
                        choices: [],
                    },
                    {
                        label: MOBILE_FILTER_LABELS[1],
                        key: MOBILE_FILTERS[1],
                        choices: [],
                    },
                ],
            },
            {
                label: MOBILE_DATASET_LABELS[1],
                dataset: new AtlasMobileAreaDataset(),
                filter: new Filter(),
                filters: [
                    {
                        label: MOBILE_FILTER_LABELS[0],
                        key: MOBILE_FILTERS[0],
                        choices: [],
                    },
                    {
                        label: MOBILE_FILTER_LABELS[1],
                        key: MOBILE_FILTERS[1],
                        choices: [],
                    },
                    {
                        label: MOBILE_FILTER_LABELS[2],
                        key: MOBILE_FILTERS[2],
                        choices: [],
                    },
                    {
                        label: MOBILE_FILTER_LABELS[3],
                        key: MOBILE_FILTERS[3],
                        choices: [],
                    },
                    {
                        label: MOBILE_FILTER_LABELS[4],
                        key: MOBILE_FILTERS[4],
                        choices: [],
                    },
                ],
            },
        ];

        this.olMapService.dataset = this.olMapService.datasets[0].dataset;
        this.olMapService.filter = this.olMapService.datasets[0].filter;
        this.olMapService.filters = this.olMapService.datasets[0].filters;
    }
}
